

import { useEffect, useState } from 'react';
import OneSignal from 'react-onesignal';

import * as Sentry from '@sentry/react';

export const useOneSignal = () => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initOneSignal = async () => {
      try {
        // Check if we're in a secure context (HTTPS or localhost)
        if (typeof window !== 'undefined' && !window.isSecureContext && window.location.hostname !== 'localhost') {
          console.warn('OneSignal requires HTTPS or localhost');
          return;
        }

        // Check if IndexedDB is available
        if (!window.indexedDB) {
          const error = new Error('IndexedDB not available');
          if (Sentry) {
            Sentry.captureException(error, {
              tags: { component: 'useOneSignal', phase: 'indexeddb_check' },
              extra: {
                userAgent: navigator.userAgent,
                isSecureContext: window.isSecureContext,
                hostname: window.location.hostname,
                protocol: window.location.protocol
              }
            });
          }
          console.warn('IndexedDB not available, OneSignal will not initialize');
          return;
        }

        // Get storage quota information for debugging
        let storageQuota = null;
        try {
          if ('storage' in navigator && 'estimate' in navigator.storage) {
            storageQuota = await navigator.storage.estimate();
          }
        } catch (quotaError) {
          console.warn('Could not get storage quota:', quotaError);
        }

        // Test IndexedDB access before initializing OneSignal
        try {
          await new Promise((resolve, reject) => {
            const testDB = window.indexedDB.open('onesignal-test-db', 1);
            
            testDB.onsuccess = (event) => {
              const db = (event.target as IDBOpenDBRequest).result;
              db.close();
              
              // Clean up test database
              const deleteReq = window.indexedDB.deleteDatabase('onesignal-test-db');
              deleteReq.onsuccess = () => resolve(true);
              deleteReq.onerror = () => resolve(true); // Still resolve, deletion failure is not critical
            };
            
            testDB.onerror = (event) => {
              const error = (event.target as IDBOpenDBRequest).error;
              reject(error);
            };
            
            testDB.onblocked = () => {
              reject(new Error('IndexedDB test blocked'));
            };
            
            testDB.onupgradeneeded = (event) => {
              const db = (event.target as IDBOpenDBRequest).result;
              if (!db.objectStoreNames.contains('test-store')) {
                db.createObjectStore('test-store');
              }
            };
          });
        } catch (dbError: any) {
          if (Sentry) {
            Sentry.captureException(dbError, {
              tags: { 
                component: 'useOneSignal', 
                phase: 'indexeddb_test',
                error_name: dbError?.name || 'unknown',
                error_code: dbError?.code || 'unknown'
              },
              extra: {
                errorMessage: dbError?.message,
                errorStack: dbError?.stack,
                userAgent: navigator.userAgent,
                isSecureContext: window.isSecureContext,
                hostname: window.location.hostname,
                protocol: window.location.protocol,
                storageQuota,
                cookiesEnabled: navigator.cookieEnabled,
                onLine: navigator.onLine,
                hardwareConcurrency: navigator.hardwareConcurrency,
                deviceMemory: (navigator as any).deviceMemory,
                connection: (navigator as any).connection ? {
                  effectiveType: (navigator as any).connection.effectiveType,
                  downlink: (navigator as any).connection.downlink,
                  rtt: (navigator as any).connection.rtt
                } : null
              }
            });
          }
          console.warn('OneSignal cannot initialize due to IndexedDB restrictions:', dbError);
          return;
        }

        // Get configuration from environment variables
        const appId = process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID;
        const safariWebId = process.env.NEXT_PUBLIC_ONESIGNAL_SAFARI_WEB_ID;

        if (!appId) {
          console.error('OneSignal App ID not configured. Please set NEXT_PUBLIC_ONESIGNAL_APP_ID in .env');
          return;
        }

        const config = {
          appId,
          safari_web_id: safariWebId,
          allowLocalhostAsSecureOrigin: true,
          notifyButton: {
            enable: true,
            size: 'medium',
            position: 'bottom-left',
            colors: {
              'circle.background': '#8A2BE2', // Purple hex color
              'circle.foreground': 'white',
              'badge.background': '#8A2BE2',
              'badge.foreground': 'white',
              'badge.bordercolor': 'white',
              'pulse.color': '#8A2BE2'
            },
            offset: {
              bottom: '20px',
              left: '20px'
            },
            showCredit: false
          },
        };

        // Initialize OneSignal
        await OneSignal.init(config);
        setInitialized(true);

        // Wait a bit before prompting to ensure full initialization
        setTimeout(async () => {
          try {
            // Check if user already has permissions
            if ('Notification' in window && Notification.permission === 'granted') {
              return;
            }

            // Try different prompt methods based on what's available
            if (OneSignal.Slidedown && OneSignal.Slidedown.promptPush) {
              await OneSignal.Slidedown.promptPush();
            } else if ((window as any).OneSignal.showSlidedownPrompt) {
              await (window as any).OneSignal.showSlidedownPrompt();
            } else if ((window as any).OneSignal.Notifications) {
              await (window as any).OneSignal.Notifications.requestPermission();
            }
          } catch (promptError) {
            console.error('Failed to show push notification prompt:', promptError);
          }
        }, 1000);

      } catch (error: any) {
        // Handle IndexedDB-specific errors gracefully
        if (error instanceof DOMException &&
            (error.name === 'UnknownError' ||
             error.name === 'QuotaExceededError' ||
             error.message?.includes('indexedDB'))) {
          
          if (Sentry) {
            Sentry.captureException(error, {
              tags: { 
                component: 'useOneSignal', 
                phase: 'onesignal_init',
                error_type: 'indexeddb_related',
                error_name: error.name
              },
              extra: {
                errorMessage: error.message,
                errorStack: error.stack,
                errorCode: error.code,
                userAgent: navigator.userAgent,
                isSecureContext: window.isSecureContext,
                hostname: window.location.hostname,
                protocol: window.location.protocol,
                oneSignalConfig: {
                  appId: process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID ? 'present' : 'missing',
                  safariWebId: process.env.NEXT_PUBLIC_ONESIGNAL_SAFARI_WEB_ID ? 'present' : 'missing'
                }
              }
            });
          }
          
          console.warn('OneSignal cannot initialize due to IndexedDB restrictions:', error.message);
          return; // Fail silently for storage-related issues
        }
        
        // Capture all other OneSignal initialization errors
        if (Sentry) {
          Sentry.captureException(error, {
            tags: { 
              component: 'useOneSignal', 
              phase: 'onesignal_init',
              error_type: 'general'
            },
            extra: {
              errorMessage: error?.message,
              errorStack: error?.stack,
              userAgent: navigator.userAgent,
              isSecureContext: window.isSecureContext,
              hostname: window.location.hostname,
              protocol: window.location.protocol
            }
          });
        }
        
        console.error('OneSignal initialization failed:', error);
      }
    };

    // Only initialize OneSignal on client side and if not already initialized
    if (typeof window !== 'undefined' && !initialized) {
      // Add a small delay to ensure the DOM and all scripts are loaded
      const timer = setTimeout(() => {
        initOneSignal();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [initialized]);

  return { initialized };
};